import { useState, useEffect, useCallback, useRef } from 'react'
import {
  X, HardDrive, Cpu, Folder, FileImage, FileVideo, File as FileIcon,
  ChevronRight, RefreshCw, Power, Camera, Loader2, AlertCircle, Download, Mic,
} from 'lucide-react'

/**
 * Detail view for one kiosk machine, driven entirely through the device agent.
 *
 * Everything here is a round trip to a Windows box on a venue hotspot, so each
 * section fails independently and says so: an agentless or powered-off kiosk
 * should show a clear "not connected" rather than an empty panel or a spinner
 * that never resolves.
 */

export interface DeviceStats {
  appVersion?: string
  platform?: string
  release?: string
  hostname?: string
  osUptime?: number
  processUptime?: number
  cpu?: { model?: string; cores?: number; usage?: number | null }
  memory?: { total: number; free: number; used: number }
  disk?: { path?: string; total: number | null; free: number | null; used: number | null }
  hqRoot?: string
  serverUrl?: string
}

interface MediaEntry {
  name: string
  relPath: string
  kind: 'dir' | 'image' | 'video' | 'other'
  size: number | null
  modified: string
}

interface Props {
  deviceId: string
  boothLabel: string
  online: boolean
  stats: DeviceStats | null
  request: (deviceId: string, op: string, params?: Record<string, unknown>) => Promise<any>
  onClose: () => void
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

function formatUptime(seconds: number | undefined): string {
  if (!seconds) return '—'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d) return `${d}d ${h}h`
  if (h) return `${h}h ${m}m`
  return `${m}m`
}

function Meter({ used, total, label }: { used: number | null; total: number | null; label: string }) {
  const pct = used !== null && total ? Math.round((used / total) * 100) : null
  // Disk filling up is the failure that silently ends an event, so it earns a
  // colour change well before it is actually full.
  const tone = pct === null ? 'bg-muted-foreground/40' : pct > 90 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-primary'
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {formatBytes(used)} / {formatBytes(total)}{pct !== null && ` · ${pct}%`}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full rounded-full transition-all ${tone}`} style={{ width: `${pct ?? 0}%` }} />
      </div>
    </div>
  )
}

export default function DevicePanel({ deviceId, boothLabel, online, stats, request, onClose }: Props) {
  const [tab, setTab] = useState<'system' | 'media' | 'camera'>('system')

  // Media browser
  const [mediaPath, setMediaPath] = useState('')
  const [entries, setEntries] = useState<MediaEntry[]>([])
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [mediaLoading, setMediaLoading] = useState(false)
  const [thumbs, setThumbs] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState<{ url: string; name: string; kind: string } | null>(null)
  const [fetchingFile, setFetchingFile] = useState<string | null>(null)

  // Camera
  const [frame, setFrame] = useState<{ dataUrl: string; micLevel: number | null; capturedAt: string } | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [liveView, setLiveView] = useState(false)
  const liveTimer = useRef<ReturnType<typeof setInterval> | null>(null)

  const [busy, setBusy] = useState<string | null>(null)

  const loadMedia = useCallback(async (relPath: string) => {
    setMediaLoading(true)
    setMediaError(null)
    try {
      const data = await request(deviceId, 'media.list', { relPath })
      setEntries(data.entries || [])
      setMediaPath(data.relPath || '')
      if (data.missing) setMediaError('The local archive folder does not exist on this machine yet.')
    } catch (err: any) {
      setMediaError(err?.message || 'Could not read the local archive')
      setEntries([])
    } finally {
      setMediaLoading(false)
    }
  }, [deviceId, request])

  useEffect(() => {
    if (tab === 'media' && entries.length === 0 && !mediaError) loadMedia('')
    // Only on first switch to the tab — re-running on every entries change
    // would refetch endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  // Thumbnails are fetched one at a time after a listing lands. Firing them all
  // at once would put dozens of multi-hundred-KB round trips on a hotspot at
  // the same moment as whatever the booth is uploading.
  useEffect(() => {
    let cancelled = false
    const images = entries.filter((e) => e.kind === 'image' && !thumbs[e.relPath])
    if (images.length === 0) return

    ;(async () => {
      for (const entry of images) {
        if (cancelled) return
        try {
          const data = await request(deviceId, 'media.thumb', { relPath: entry.relPath })
          if (cancelled) return
          setThumbs((prev) => ({ ...prev, [entry.relPath]: data.dataUrl }))
        } catch {
          // A single unreadable file shouldn't stop the rest of the listing.
        }
      }
    })()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, deviceId])

  const openEntry = async (entry: MediaEntry) => {
    if (entry.kind === 'dir') {
      setThumbs({})
      loadMedia(entry.relPath)
      return
    }
    if (entry.kind === 'other') return

    setFetchingFile(entry.relPath)
    try {
      const data = await request(deviceId, 'media.get', { relPath: entry.relPath })
      setPreview({ url: data.url, name: entry.name, kind: entry.kind })
    } catch (err: any) {
      setMediaError(err?.message || 'Could not fetch that file from the device')
    } finally {
      setFetchingFile(null)
    }
  }

  const snapshot = useCallback(async () => {
    try {
      const data = await request(deviceId, 'camera.snapshot', { maxWidth: 720, quality: 0.5 })
      setFrame(data)
      setCameraError(null)
    } catch (err: any) {
      setCameraError(err?.message || 'Could not get a camera frame')
      setLiveView(false)
    }
  }, [deviceId, request])

  // Polled rather than streamed, and only while the operator is actually
  // looking: a continuous feed off a venue hotspot would compete with the
  // booth's own uploads for the whole event.
  useEffect(() => {
    if (!liveView) {
      if (liveTimer.current) clearInterval(liveTimer.current)
      liveTimer.current = null
      return
    }
    snapshot()
    liveTimer.current = setInterval(snapshot, 2000)
    return () => {
      if (liveTimer.current) clearInterval(liveTimer.current)
      liveTimer.current = null
    }
  }, [liveView, snapshot])

  const runControl = async (op: string, label: string) => {
    setBusy(label)
    try {
      await request(deviceId, op)
    } catch (err: any) {
      setMediaError(err?.message || `${label} failed`)
    } finally {
      setBusy(null)
    }
  }

  const crumbs = mediaPath ? mediaPath.split('/').filter(Boolean) : []

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-bold">
              <span className={`h-2 w-2 rounded-full ${online ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
              {boothLabel}
            </h2>
            <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{deviceId}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        {!online ? (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-muted-foreground/40" />
            <h3 className="font-semibold text-muted-foreground">No agent connected</h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              This booth is reporting from its page, but the machine itself isn't reachable.
              It's either running a client build without the device agent, or the agent has no
              application password configured.
            </p>
          </div>
        ) : (
          <>
            <div className="flex gap-1 border-b px-4">
              {(['system', 'media', 'camera'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors ${
                    tab === t ? 'border-b-2 border-primary text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t === 'media' ? 'Local files' : t}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {tab === 'system' && (
                <div className="space-y-5">
                  {!stats ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Waiting for the first report…
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                        {[
                          { label: 'Client version', value: stats.appVersion || '—' },
                          { label: 'Machine', value: stats.hostname || '—' },
                          { label: 'App uptime', value: formatUptime(stats.processUptime) },
                          { label: 'Machine uptime', value: formatUptime(stats.osUptime) },
                        ].map((item) => (
                          <div key={item.label} className="rounded-xl border bg-muted/20 p-3">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{item.label}</div>
                            <div className="mt-1 truncate text-sm font-semibold" title={item.value}>{item.value}</div>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-4 rounded-xl border p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                          <Cpu size={15} className="text-muted-foreground" /> Resources
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">CPU {stats.cpu?.cores ? `(${stats.cpu.cores} cores)` : ''}</span>
                            <span className="font-medium">{stats.cpu?.usage === null || stats.cpu?.usage === undefined ? '—' : `${stats.cpu.usage}%`}</span>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${stats.cpu?.usage ?? 0}%` }} />
                          </div>
                        </div>
                        <Meter label="Memory" used={stats.memory?.used ?? null} total={stats.memory?.total ?? null} />
                        <Meter label="Disk" used={stats.disk?.used ?? null} total={stats.disk?.total ?? null} />
                        {stats.disk?.free !== null && stats.disk?.free !== undefined && (
                          <p className="text-xs text-muted-foreground">
                            <HardDrive size={12} className="mr-1 inline" />
                            {formatBytes(stats.disk.free)} free on {stats.disk.path}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2 rounded-xl border p-4 text-xs">
                        <div className="flex justify-between gap-4">
                          <span className="text-muted-foreground">Local archive</span>
                          <span className="truncate font-mono" title={stats.hqRoot}>{stats.hqRoot || '—'}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span className="text-muted-foreground">Server</span>
                          <span className="truncate font-mono" title={stats.serverUrl}>{stats.serverUrl || '—'}</span>
                        </div>
                        <div className="flex justify-between gap-4">
                          <span className="text-muted-foreground">Platform</span>
                          <span className="font-mono">{stats.platform} {stats.release}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => runControl('window.reload', 'Reload')}
                          disabled={!!busy}
                          className="flex items-center gap-2 rounded-lg border bg-secondary px-3 py-2 text-xs font-medium hover:bg-secondary/80 disabled:opacity-50"
                        >
                          <RefreshCw size={14} /> Reload page
                        </button>
                        <button
                          onClick={() => runControl('app.relaunch', 'Relaunch')}
                          disabled={!!busy}
                          className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive hover:bg-destructive/20 disabled:opacity-50"
                        >
                          <Power size={14} /> Restart client
                        </button>
                        {busy && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> {busy}…</span>}
                      </div>
                    </>
                  )}
                </div>
              )}

              {tab === 'media' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-1 text-xs">
                    <button onClick={() => { setThumbs({}); loadMedia('') }} className="rounded px-2 py-1 font-medium hover:bg-muted">
                      Archive
                    </button>
                    {crumbs.map((crumb, i) => (
                      <span key={i} className="flex items-center gap-1">
                        <ChevronRight size={12} className="text-muted-foreground" />
                        <button
                          onClick={() => { setThumbs({}); loadMedia(crumbs.slice(0, i + 1).join('/')) }}
                          className="rounded px-2 py-1 font-medium hover:bg-muted"
                        >
                          {crumb}
                        </button>
                      </span>
                    ))}
                  </div>

                  {mediaError && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-600 dark:text-amber-400">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" /> {mediaError}
                    </div>
                  )}

                  {mediaLoading ? (
                    <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Reading the device…
                    </div>
                  ) : entries.length === 0 ? (
                    <p className="py-10 text-center text-sm text-muted-foreground">Nothing here.</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {entries.map((entry) => (
                        <button
                          key={entry.relPath}
                          onClick={() => openEntry(entry)}
                          className="group flex flex-col overflow-hidden rounded-xl border bg-muted/20 text-left transition-colors hover:bg-muted/50"
                        >
                          <div className="flex aspect-video items-center justify-center overflow-hidden bg-muted/40">
                            {fetchingFile === entry.relPath ? (
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            ) : thumbs[entry.relPath] ? (
                              <img src={thumbs[entry.relPath]} alt={entry.name} className="h-full w-full object-cover" />
                            ) : entry.kind === 'dir' ? (
                              <Folder className="h-7 w-7 text-muted-foreground/50" />
                            ) : entry.kind === 'video' ? (
                              <FileVideo className="h-7 w-7 text-muted-foreground/50" />
                            ) : entry.kind === 'image' ? (
                              <FileImage className="h-7 w-7 animate-pulse text-muted-foreground/30" />
                            ) : (
                              <FileIcon className="h-7 w-7 text-muted-foreground/50" />
                            )}
                          </div>
                          <div className="p-2">
                            <div className="truncate text-xs font-medium" title={entry.name}>{entry.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {entry.kind === 'dir' ? 'Folder' : formatBytes(entry.size)}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === 'camera' && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={snapshot}
                      className="flex items-center gap-2 rounded-lg border bg-secondary px-3 py-2 text-xs font-medium hover:bg-secondary/80"
                    >
                      <Camera size={14} /> Take snapshot
                    </button>
                    <button
                      onClick={() => setLiveView((v) => !v)}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                        liveView ? 'border-red-500/30 bg-red-500/10 text-red-500' : 'bg-secondary hover:bg-secondary/80'
                      }`}
                    >
                      {liveView ? <><span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> Stop live view</> : <>Live view (2s)</>}
                    </button>
                  </div>

                  <p className="text-[11px] text-muted-foreground">
                    Guests see an on-screen notice while the camera is being viewed remotely.
                  </p>

                  {cameraError && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-600 dark:text-amber-400">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" /> {cameraError}
                    </div>
                  )}

                  {frame ? (
                    <div className="space-y-2">
                      <img src={frame.dataUrl} alt="Booth camera" className="w-full rounded-xl border" />
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>{new Date(frame.capturedAt).toLocaleTimeString()}</span>
                        {frame.micLevel !== null && frame.micLevel !== undefined && (
                          <span className="flex items-center gap-2">
                            <Mic size={12} />
                            <span className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                              <span className="block h-full rounded-full bg-primary transition-all" style={{ width: `${frame.micLevel}%` }} />
                            </span>
                            {frame.micLevel}%
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    !cameraError && <p className="py-10 text-center text-sm text-muted-foreground">No frame yet.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Full-size preview of a file pulled off the device */}
      {preview && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/85 p-6" onClick={(e) => { e.stopPropagation(); setPreview(null) }}>
          <div className="flex max-h-full w-full max-w-4xl flex-col gap-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white">
              <span className="truncate text-sm font-medium">{preview.name}</span>
              <div className="flex items-center gap-2">
                <a href={preview.url} download={preview.name} className="rounded-lg bg-white/10 p-2 hover:bg-white/20" title="Download">
                  <Download size={16} />
                </a>
                <button onClick={() => setPreview(null)} className="rounded-lg bg-white/10 p-2 hover:bg-white/20">
                  <X size={16} />
                </button>
              </div>
            </div>
            {preview.kind === 'video' ? (
              <video src={preview.url} controls autoPlay className="max-h-[75vh] w-full rounded-xl bg-black" />
            ) : (
              <img src={preview.url} alt={preview.name} className="max-h-[75vh] w-full rounded-xl object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
